import React, { MouseEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser, isP1PlusNotFreeUser } from '@modules/auth/types'
import ContactAttendeesPopover from '@modules/chat/components/ContactAttendeesPopover'
import ContactedBubble from '@modules/chat/components/ContactedBubble'
import { useChatContext } from '@modules/chat/useChatContext'
import ContactsOverviewCardStateFull from '@modules/contacts/components/ContactsOverviewCardStateFull'
import { AnyResultProfile, Company, CompanyHistoryProfile, ProfileType } from '@modules/search/types'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'

import ConferenceAttendees from '../ConferenceAttendees'
import { ProfileElements, SectionProps } from '../ProfileDetails'
import useAttendees from '../hooks/useAttendees'
import useFetchProfile from '../hooks/useFetchProfile'
import {
  CompanyCardInner,
  CompanyCardName,
  CompanyCardTitle,
  CompanyCardTitleWrap,
  CompanyCardWrap,
  CompanyContactButton,
  CompanyProfileButton,
  CompanyProfileButtonWrapper,
  SkeletonWrap,
  StyledProfileLogo,
} from '../index.styles'

const CompanyCardConfig = [
  {
    subSections: [
      {
        subTitle: 'COMPANY_CARD_SUMMARY',
        fieldName: 'description',
        shouldParse: true,
        truncateToLines: 5,
      },
    ],
  },
  {
    subSections: [
      {
        subTitle: 'COMPANY_CARD_MAIN_SECTOR',
        fieldName: 'sector',
      },
    ],
  },
  {
    subSections: [
      {
        subTitle: 'COMPANY_CARD_SUBSECTOR',
        fieldName: 'subSectors',
        isList: true,
      },
    ],
  },
  {
    subSections: [
      {
        subTitle: 'COMPANY_CARD_THERAPEUTIC_AREA',
        fieldName: 'primaryTherapeuticAreas',
        isList: true,
      },
    ],
  },
  {
    subSections: [
      {
        fieldName: 'attendees',
        renderComponent: ({ profileId }: { profileId: number }) => <ConferenceAttendees companyId={profileId} smallViewMode />,
      },
    ],
  },
  {
    subSections: [
      {
        subTitle: 'COMPANY_CARD_CONTACT_DETAILS',
        joinedFieldsKey: 'fullAddress',
        joinedFieldsConfig: [
          {
            fieldName: 'addressLine1',
            joinSymbol: '',
            isNewLine: true,
          },
          {
            fieldName: 'addressLine2',
            joinSymbol: '',
          },
          {
            fieldName: 'zip',
            joinSymbol: ', ',
          },
          {
            fieldName: 'city',
            joinSymbol: ', ',
          },
          {
            fieldName: 'country',
            joinSymbol: ', ',
          },
          {
            fieldName: 'state',
            joinSymbol: ', ',
          },
          {
            fieldName: 'profileData.phone',
            joinSymbol: '',
            isBold: true,
            isNewLine: true,
          },
        ],
      },
    ],
  },
  {
    subSections: [
      {
        fieldName: 'profileData.email',
        isEmail: true,
      },
    ],
  },
  {
    subSections: [
      {
        fieldName: 'website',
        isLink: true,
      },
    ],
  },
]

const CompanyCard = ({ companyId }: { companyId: number }) => {
  const { t } = useTranslation()
  const { openChatModal } = useChatContext()
  const { user } = useAuthContext()
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)
  const isNotFreeUser = useMemo(() => isP1PlusNotFreeUser(user?.subscriptionLevel || 0), [user])
  const { hasAccessToChat } = useFeatureFlags()
  const navigate = useNavigate()

  const {
    data: fullCompanyProfile,
    isLoading,
    isError,
  } = useFetchProfile({ type: ProfileType.company, id: companyId, disabled: true })

  const { latest: companyData = {} as Company } = fullCompanyProfile || ({} as CompanyHistoryProfile)

  const { total: totalAttendees, hasContactedWithCompany } = useAttendees({ companyId })

  const handleCompanyProfile = () => {
    navigate(`/plus/profile/${companyId}/company`)
  }

  const [contactAttendeesPopoverData, setContactAttendeesPopoverData] = useState<
    Nullable<{
      anchor?: Nullable<HTMLElement>
      profile?: AnyResultProfile
    }>
  >(null)

  const onContactClick = (e: MouseEvent<HTMLElement>) => {
    if (!fullCompanyProfile) {
      return null
    }

    if (e?.currentTarget) {
      setContactAttendeesPopoverData({ anchor: e?.currentTarget, profile: fullCompanyProfile })
      return undefined
    }

    return setContactAttendeesPopoverData(null)
  }

  return (
    <CompanyCardWrap>
      <CompanyCardInner>
        {isError && <div>{t(translationKeys.FETCHING_ERROR)}</div>}
        {isLoading && (
          <>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
          </>
        )}
        {companyData && (
          <>
            <CompanyCardTitleWrap>
              <StyledProfileLogo
                profileData={companyData}
                profileType={ProfileType.company}
                width={40}
                defaultLogoSize={25}
                circleView
              />
              <CompanyCardTitle variant="h3">{t(translationKeys.COMPANY_CARD_OVERVIEW)}</CompanyCardTitle>
            </CompanyCardTitleWrap>
            <CompanyCardName variant="h2" onClick={handleCompanyProfile}>
              {companyData?.name}
            </CompanyCardName>
            <Box mb="16px">
              <ContactsOverviewCardStateFull profile={companyData} />
            </Box>

            {totalAttendees > 0 && hasAccessToChat && !isEcommerce && (
              <ContactedBubble hasContacted={hasContactedWithCompany}>
                <CompanyContactButton variant="contained" onClick={onContactClick}>
                  {t(translationKeys.CONTACT_BUTTON)}
                </CompanyContactButton>
              </ContactedBubble>
            )}

            <ProfileElements
              profileSectionConfig={CompanyCardConfig as SectionProps[]}
              profileData={companyData}
              profileId={companyId}
              profileAreaName="companyCard"
              hasAccessToAlerts={isNotFreeUser}
            />

            <CompanyProfileButtonWrapper>
              <CompanyProfileButton onClick={handleCompanyProfile} variant="contained">
                {t('COMPANY_CARD_PROFILE_BUTTON')}
              </CompanyProfileButton>
            </CompanyProfileButtonWrapper>
          </>
        )}
      </CompanyCardInner>
      {!!contactAttendeesPopoverData?.profile && (
        <ContactAttendeesPopover
          anchor={contactAttendeesPopoverData?.anchor}
          profile={contactAttendeesPopoverData?.profile}
          close={() => setContactAttendeesPopoverData(null)}
          openChatModal={openChatModal}
        />
      )}
    </CompanyCardWrap>
  )
}

export default CompanyCard
